namespace $ {

	export type $shm_projects_graph_module = {
		id: string
		description?: string
		paths: readonly string[]
		includes?: readonly string[]
		uses?: readonly string[]
		project?: string
	}

	export type $shm_projects_graph_build = {
		id: string
		modules: readonly string[]
		paths?: readonly string[]
		kind: 'image' | 'files'
		bundle: $shm_projects_release_bundle
		image?: string
		version_file?: string
	}

	export type $shm_projects_graph_component = Partial< $shm_projects_release_component > & {
		id: string
		build?: string
	}

	export class $shm_projects_graph {

		readonly module_by_id = new Map< string, $shm_projects_graph_module >()
		readonly build_by_id = new Map< string, $shm_projects_graph_build >()

		constructor(
			modules: readonly $shm_projects_graph_module[],
			builds: readonly $shm_projects_graph_build[],
			projects: readonly string[],
		) {
			const project_ids = new Set( projects )
			for( const module of modules ) {
				if( !module.id.trim() ) throw new Error( 'Module id is required' )
				if( this.module_by_id.has( module.id ) ) throw new Error( `Duplicate module id: ${ module.id }` )
				if( !module.paths.length ) throw new Error( `Module paths are required: ${ module.id }` )
				if( module.project && !project_ids.has( module.project ) ) throw new Error( `Module project not found for ${ module.id }: ${ module.project }` )
				this.module_by_id.set( module.id, module )
			}
			for( const module of modules ) {
				for( const dependency of [ ...( module.includes ?? [] ), ...( module.uses ?? [] ) ] ) {
					if( !this.module_by_id.has( dependency ) ) throw new Error( `Module dependency not found for ${ module.id }: ${ dependency }` )
				}
			}
			for( const build of builds ) {
				if( !build.id.trim() ) throw new Error( 'Build id is required' )
				if( this.build_by_id.has( build.id ) ) throw new Error( `Duplicate build id: ${ build.id }` )
				if( !build.modules.length ) throw new Error( `Build modules are required: ${ build.id }` )
				for( const module of build.modules ) {
					if( !this.module_by_id.has( module ) ) throw new Error( `Build module not found for ${ build.id }: ${ module }` )
				}
				this.build_by_id.set( build.id, build )
			}
		}

		normalize( path: string ) {
			return path.replace( /\\/g, '/' ).replace( /^\.\//, '' ).replace( /\/$/, '' )
		}

		path_matches( path_raw: string, scope_raw: string ) {
			const path = this.normalize( path_raw )
			const scope = this.normalize( scope_raw )
			return path === scope || path.startsWith( `${ scope }/` ) || scope.startsWith( `${ path }/` )
		}

		modules_changed( paths: readonly string[] ) {
			return [ ... this.module_by_id.values() ].filter( module =>
				module.paths.some( scope => paths.some( path => this.path_matches( path, scope ) ) )
			).map( module => module.id )
		}

		modules_affected( paths: readonly string[] ) {
			const found = new Set( this.modules_changed( paths ) )
			let changed = true
			while( changed ) {
				changed = false
				for( const module of this.module_by_id.values() ) {
					if( found.has( module.id ) ) continue
					if( [ ...( module.includes ?? [] ), ...( module.uses ?? [] ) ].some( dependency => found.has( dependency ) ) ) {
						found.add( module.id )
						changed = true
					}
				}
			}
			return [ ...found ]
		}

		dependencies( roots: readonly string[] ) {
			const found = new Set< string >()
			const visit = ( id: string )=> {
				if( found.has( id ) ) return
				found.add( id )
				const module = this.module_by_id.get( id )!
				for( const dependency of [ ...( module.includes ?? [] ), ...( module.uses ?? [] ) ] ) visit( dependency )
			}
			for( const root of roots ) visit( root )
			return found
		}

		build_paths( build: $shm_projects_graph_build ) {
			const paths = new Set( ( build.paths ?? [] ).map( path => this.normalize( path ) ) )
			for( const id of this.dependencies( build.modules ) ) {
				for( const path of this.module_by_id.get( id )!.paths ) paths.add( this.normalize( path ) )
			}
			return [ ...paths ]
		}

		component( source: $shm_projects_graph_component ) {
			if( !source.build ) return source as $shm_projects_release_component
			const build = this.build_by_id.get( source.build )
			if( !build ) throw new Error( `Component build not found for ${ source.id }: ${ source.build }` )
			const { build: _, ... overrides } = source
			const component = {
				... build,
				... overrides,
				id: source.id,
				paths: [ ...new Set( [ ...this.build_paths( build ), ...( source.paths ?? [] ).map( path => this.normalize( path ) ) ] ) ],
			} as $shm_projects_release_component
			if( !component.kind || !component.bundle ) throw new Error( `Component build is incomplete: ${ source.id }` )
			return component
		}

	}

}
